/**
 * Admin Language Management
 * Manage languages and translations
 */

import React, { useState, useEffect } from 'react';
import {
  Globe,
  Plus,
  Edit,
  Trash2,
  Download,
  Upload,
  CheckCircle,
  Copy,
  BarChart,
} from 'lucide-react';

interface Language {
  id: number;
  code: string;
  name: string;
  native_name: string;
  direction: 'ltr' | 'rtl';
  is_enabled: number;
  is_default: number;
  flag_emoji: string | null;
}

interface TranslationStats {
  totalKeys: number;
  translatedKeys: number;
  missingKeys: number;
  completionPercentage: number;
}

export const Languages: React.FC = () => {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<Language | null>(null);
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [stats, setStats] = useState<TranslationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'languages' | 'translations'>('languages');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadLanguages();
  }, []);

  useEffect(() => {
    if (selectedLanguage) {
      loadTranslations(selectedLanguage.code);
      loadStats(selectedLanguage.code);
    }
  }, [selectedLanguage]);

  const loadLanguages = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/admin/languages?includeDisabled=true', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setLanguages(data.data.languages);
        if (!selectedLanguage && data.data.languages.length > 0) {
          setSelectedLanguage(data.data.languages[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load languages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTranslations = async (languageCode: string) => {
    try {
      const response = await fetch(`/api/v1/admin/languages/${languageCode}/translations`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setTranslations(data.data.translations);
      }
    } catch (error) {
      console.error('Failed to load translations:', error);
    }
  };

  const loadStats = async (languageCode: string) => {
    try {
      const response = await fetch(
        `/api/v1/admin/languages/${languageCode}/translations/statistics`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setStats(data.data.statistics);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handleSetDefault = async (languageId: number) => {
    if (!confirm('Set this language as default?')) return;

    try {
      const response = await fetch(`/api/v1/admin/languages/${languageId}/set-default`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadLanguages();
      }
    } catch (error) {
      console.error('Failed to set default language:', error);
    }
  };

  const handleDeleteLanguage = async (languageId: number) => {
    if (!confirm('Are you sure you want to delete this language? All translations will be lost.'))
      return;

    try {
      const response = await fetch(`/api/v1/admin/languages/${languageId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadLanguages();
        if (selectedLanguage?.id === languageId) {
          setSelectedLanguage(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete language:', error);
    }
  };

  const handleExportTranslations = async () => {
    if (!selectedLanguage) return;

    try {
      const response = await fetch(
        `/api/v1/admin/languages/${selectedLanguage.code}/translations/export`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedLanguage.code}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export translations:', error);
    }
  };

  const filteredTranslations = Object.entries(translations).filter(
    ([key, value]) =>
      searchQuery === '' ||
      key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      value.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Globe className="w-6 h-6" />
              Languages & Translations
            </h1>
            <p className="text-slate-400 mt-1">Manage multi-language support</p>
          </div>
          <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Language
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-slate-700/50">
        <button
          onClick={() => setSelectedTab('languages')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'languages'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Languages ({languages.length})
        </button>
        <button
          onClick={() => setSelectedTab('translations')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'translations'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
          disabled={!selectedLanguage}
        >
          Translations
          {selectedLanguage && ` (${selectedLanguage.name})`}
        </button>
      </div>

      {/* Languages Tab */}
      {selectedTab === 'languages' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Language List */}
          <div className="lg:col-span-2">
            <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="text-left p-4 text-slate-300 font-medium">Language</th>
                    <th className="text-left p-4 text-slate-300 font-medium">Code</th>
                    <th className="text-left p-4 text-slate-300 font-medium">Direction</th>
                    <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                    <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-slate-400">
                        Loading...
                      </td>
                    </tr>
                  ) : languages.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-slate-400">
                        No languages found
                      </td>
                    </tr>
                  ) : (
                    languages.map((language) => (
                      <tr
                        key={language.id}
                        className={`hover:bg-slate-700/30 cursor-pointer ${
                          selectedLanguage?.id === language.id ? 'bg-gray-750' : ''
                        }`}
                        onClick={() => setSelectedLanguage(language)}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{language.flag_emoji}</span>
                            <div>
                              <div className="text-white font-medium flex items-center gap-2">
                                {language.name}
                                {language.is_default === 1 && (
                                  <span className="px-2 py-0.5 bg-orange-900 text-blue-300 rounded text-xs">
                                    DEFAULT
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-slate-400">
                                {language.native_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-300 font-mono text-sm">
                            {language.code.toUpperCase()}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-300 text-sm">
                            {language.direction.toUpperCase()}
                          </span>
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-2 py-1 rounded text-sm ${
                              language.is_enabled
                                ? 'bg-green-900 text-green-300'
                                : 'bg-slate-700/50 text-slate-400'
                            }`}
                          >
                            {language.is_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            {language.is_default === 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSetDefault(language.id);
                                }}
                                className="p-2 text-green-400 hover:bg-slate-700/50 rounded"
                                title="Set as Default"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {language.is_default === 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteLanguage(language.id);
                                }}
                                className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Statistics Sidebar */}
          {selectedLanguage && stats && (
            <div className="space-y-6">
              <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <BarChart className="w-5 h-5" />
                  Translation Progress
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">Completion</span>
                      <span className="text-white font-medium">
                        {stats.completionPercentage.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-700/50 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${stats.completionPercentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-750 rounded-lg p-3">
                      <div className="text-slate-400 text-xs mb-1">Translated</div>
                      <div className="text-white text-xl font-bold">
                        {stats.translatedKeys}
                      </div>
                    </div>
                    <div className="bg-gray-750 rounded-lg p-3">
                      <div className="text-slate-400 text-xs mb-1">Missing</div>
                      <div className="text-white text-xl font-bold">{stats.missingKeys}</div>
                    </div>
                  </div>
                  <div className="bg-gray-750 rounded-lg p-3">
                    <div className="text-slate-400 text-xs mb-1">Total Keys</div>
                    <div className="text-white text-xl font-bold">{stats.totalKeys}</div>
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-medium text-white mb-4">Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={handleExportTranslations}
                    className="w-full px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export JSON
                  </button>
                  <button className="w-full px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 flex items-center justify-center gap-2">
                    <Upload className="w-4 h-4" />
                    Import JSON
                  </button>
                  <button className="w-full px-4 py-2 bg-slate-700/50 text-white rounded-lg hover:bg-slate-600/50 flex items-center justify-center gap-2">
                    <Copy className="w-4 h-4" />
                    Copy from...
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Translations Tab */}
      {selectedTab === 'translations' && selectedLanguage && (
        <div>
          {/* Search */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search translations..."
              className="w-full px-4 py-2 bg-slate-700/50 border border-gray-600 rounded-lg text-white"
            />
          </div>

          {/* Translations Table */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium w-1/3">Key</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Translation</th>
                  <th className="text-right p-4 text-slate-300 font-medium w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {filteredTranslations.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center py-8 text-slate-400">
                      No translations found
                    </td>
                  </tr>
                ) : (
                  filteredTranslations.map(([key, value]) => (
                    <tr key={key} className="hover:bg-slate-700/30">
                      <td className="p-4">
                        <span className="text-slate-300 font-mono text-sm">{key}</span>
                      </td>
                      <td className="p-4">
                        <span className="text-white">{value}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <button
                            className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Languages;
